/**
 * Read a human-readable message (or a machine code) off a failed API call.
 *
 * Two shapes reach UI handlers: an axios error, whose server body sits on
 * `error.response.data`, and a `success: false` body that a service re-threw
 * with the payload attached as `error.data`. Call sites historically dug into
 * those by hand in slightly different orders; the order is a parameter here so
 * each keeps the precedence (and fallback text) it had.
 *
 * Only non-empty strings count. A server that answers `{ error: { … } }` falls
 * through to the next source instead of handing an object to a toast.
 */

/**
 * Where to look, in order:
 * - `response.error` / `response.message` / `response.code`: `error.response.data.<field>`
 * - `body.error` / `body.message` / `body.code`: `error.data.<field>`
 * - `error.message`: the thrown value's own `message` (axios: "Request failed with status code 500")
 */
export type ApiErrorSource =
  | 'response.error'
  | 'response.message'
  | 'body.error'
  | 'body.message'
  | 'error.message';

export type ApiErrorCodeSource = 'response.code' | 'body.code';

/** Server `error`, then server `message`, then the thrown error's own message. */
const DEFAULT_SOURCES: readonly ApiErrorSource[] = ['response.error', 'response.message', 'error.message'];

const asRecord = (value: unknown): Record<string, unknown> | undefined => (
  value !== null && typeof value === 'object' ? value as Record<string, unknown> : undefined
);

const nonEmptyString = (value: unknown): string | undefined => (
  typeof value === 'string' && value.length > 0 ? value : undefined
);

const readSource = (error: unknown, source: ApiErrorSource | ApiErrorCodeSource): string | undefined => {
  const err = asRecord(error);
  if (!err) return undefined;
  if (source === 'error.message') return nonEmptyString(err.message);
  const [container, field] = source.split('.') as ['response' | 'body', string];
  const payload = container === 'response' ? asRecord(asRecord(err.response)?.data) : asRecord(err.data);
  return nonEmptyString(payload?.[field]);
};

/** The first non-empty message found in `sources`, else `fallback`. */
export function getApiErrorMessage(
  error: unknown,
  fallback: string,
  sources: readonly ApiErrorSource[] = DEFAULT_SOURCES,
): string {
  return findApiErrorMessage(error, sources) ?? fallback;
}

/** Like {@link getApiErrorMessage} without a fallback: `undefined` when nothing was found. */
export function findApiErrorMessage(
  error: unknown,
  sources: readonly ApiErrorSource[] = DEFAULT_SOURCES,
): string | undefined {
  for (const source of sources) {
    const message = readSource(error, source);
    if (message !== undefined) return message;
  }
  return undefined;
}

/**
 * The HTTP status of a failed call, when the thrown value carries one.
 *
 * Whether a failure was the request's fault or ours is the difference between "check this
 * contact" and "try again later", and a fallback string cannot tell them apart on its own —
 * mb-frontend#4803 R2-1 had the outreach panel answer *"Something went wrong on our side"* to a
 * 400 the request had caused.
 */
export function getApiErrorStatus(error: unknown): number | undefined {
  const status = asRecord(asRecord(error)?.response)?.status;
  return typeof status === 'number' ? status : undefined;
}

/** The server's refusal code (`response.data.code` by default), if any. */
export function getApiErrorCode(
  error: unknown,
  sources: readonly ApiErrorCodeSource[] = ['response.code'],
): string | undefined {
  for (const source of sources) {
    const code = readSource(error, source);
    if (code !== undefined) return code;
  }
  return undefined;
}
